import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { Zap, Shield, TrendingUp, ArrowRight } from 'lucide-react';
import RatesTicker from '../components/RatesTicker';
import LiveRatesTable from '../components/LiveRatesTable';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_olorire-trade/artifacts/rwd7e8nj_IMG_3182.png";
const WHATSAPP_NUMBER = "2349152474245";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const benefits = [
  {
    icon: Zap,
    title: 'Fast Transactions',
    description: 'Complete your trades in minutes, not hours. Our streamlined process ensures quick execution.'
  },
  {
    icon: TrendingUp,
    title: 'Best Market Rates',
    description: 'Get competitive rates updated in real-time. We offer some of the best prices in the market.'
  },
  {
    icon: Shield,
    title: 'Secure & Reliable',
    description: 'Your security is our priority. Trade with confidence on our trusted platform.'
  }
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 lg:px-24 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background-paper/50 to-background" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary-main/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-main/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            variants={stagger}
            initial="initial"
            animate="animate"
          >
            {/* Left Content */}
            <motion.div variants={fadeInUp}>
              <motion.div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-main/10 border border-primary-main/30 mb-6"
                whileHover={{ scale: 1.05 }}
              >
                <span className="w-2 h-2 rounded-full bg-accent-success animate-pulse" />
                <span className="text-primary-main text-sm font-medium">Live Trading Available</span>
              </motion.div>

              <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="text-text-primary">Buy & Sell</span>
                <br />
                <span className="gold-gradient-text">Crypto Instantly</span>
              </h1>

              <p className="text-text-secondary text-lg md:text-xl mb-8 max-w-lg">
                Nigeria's trusted cryptocurrency exchange. Trade Bitcoin, Ethereum, and USDT with the best rates and instant transactions.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/trade" data-testid="hero-start-trading-btn">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-gold flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    Start Trading Now
                    <ArrowRight size={18} />
                  </motion.button>
                </Link>
                
                <motion.a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello%20OX%20Olorire!%20I'm%20interested%20in%20trading.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-whatsapp flex items-center gap-2 w-full sm:w-auto justify-center"
                  data-testid="hero-whatsapp-btn"
                >
                  <FaWhatsapp size={20} />
                  WhatsApp Us
                </motion.a>
              </div>

              {/* Trust Indicators */}
              <div className="mt-10 flex items-center gap-6">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-accent-bitcoin flex items-center justify-center">
                    <FaBitcoin size={16} className="text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-accent-ethereum flex items-center justify-center">
                    <FaEthereum size={16} className="text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-accent-tether flex items-center justify-center">
                    <SiTether size={16} className="text-white" />
                  </div>
                </div>
                <p className="text-text-muted text-sm">
                  <span className="text-text-primary font-semibold">3+ Cryptos</span> supported
                </p>
              </div>
            </motion.div>

            {/* Right Content - Logo Display */}
            <motion.div
              variants={fadeInUp}
              className="hidden lg:flex justify-center items-center"
            >
              <motion.div
                animate={{ 
                  y: [0, -15, 0],
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="absolute inset-0 bg-primary-main/20 rounded-full blur-3xl scale-150" />
                <img 
                  src={LOGO_URL} 
                  alt="OX Olorire Exchange" 
                  className="relative w-80 h-auto drop-shadow-2xl"
                  data-testid="hero-logo"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Rates Ticker */}
      <RatesTicker />

      {/* Benefits Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
              <span className="text-text-primary">Why Trade With </span>
              <span className="gold-gradient-text">OX Olorire?</span>
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Experience the fastest and most reliable crypto trading in Nigeria
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass-card p-8 text-center group"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary-main/10 flex items-center justify-center group-hover:bg-primary-main/20 transition-colors">
                  <benefit.icon size={28} className="text-primary-main" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-text-secondary">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Rates Preview */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-background-paper" data-testid="rates-preview-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card-gold p-8"
          >
            <LiveRatesTable compact />
            
            <div className="mt-8 text-center">
              <Link to="/rates" data-testid="view-all-rates-btn">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 rounded-full bg-background border border-border hover:border-primary-main/50 text-text-primary transition-all inline-flex items-center gap-2"
                >
                  View All Rates
                  <ArrowRight size={16} />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card-gold p-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              <span className="text-text-primary">Ready to Start </span>
              <span className="gold-gradient-text">Trading?</span>
            </h2>
            <p className="text-text-secondary text-lg mb-8 max-w-xl mx-auto">
              Join thousands of Nigerians who trust OX Olorire Exchange for their crypto needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trade" data-testid="cta-trade-btn">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-gold"
                >
                  Trade Now
                </motion.button>
              </Link>
              <motion.a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-whatsapp inline-flex items-center gap-2"
                data-testid="cta-whatsapp-btn"
              >
                <FaWhatsapp size={20} />
                Contact on WhatsApp
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="OX Olorire Exchange" className="h-10 w-auto" />
            </div>
            <p className="text-text-muted text-sm">
              © {new Date().getFullYear()} OX Olorire Exchange. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-accent-success transition-colors"
              >
                <FaWhatsapp size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
